/**
 * Somvera Atelier — purchase logic.
 *
 * One controller owns variant selection, bundle tiers, price display, the
 * gallery, and the sticky bar. They are deliberately not separate scripts:
 * every one of them has to show the same number at the same moment, and the
 * cheapest way to guarantee that is a single source of truth.
 *
 * Reads its data from a JSON script tag rendered by the section, so Liquid
 * stays in Liquid and this file never has to parse markup for prices.
 */

(function () {
  'use strict';

  // Colour frames are committed theme assets, not product media, so the
  // controller builds their URLs from the slug rather than reading them off
  // the variant. The widths must match derive_variants.WIDTHS.
  const SV_COLOUR_WIDTHS = [400, 600, 900, 1200, 1600];

  function svColourSrc(assetBase, slug) {
    return `${assetBase}sv-photo-colour-${slug}-1200.jpg`;
  }

  function svColourSrcset(assetBase, slug) {
    return SV_COLOUR_WIDTHS
      .map((width) => `${assetBase}sv-photo-colour-${slug}-${width}.webp ${width}w`)
      .join(', ');
  }

  /** Money formatting mirrors the shop's own format string where possible. */
  function makeFormatter(moneyFormat) {
    return function (cents) {
      const amount = (cents / 100).toFixed(2);
      if (!moneyFormat) return amount;
      // Shopify money formats use {{amount}}, {{amount_with_comma_separator}} etc.
      const withComma = amount.replace('.', ',');
      return moneyFormat
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withComma.split(',')[0])
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amount.split('.')[0])
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    };
  }

  class SomveraBuy {
    constructor(root) {
      this.root = root;
      const dataEl = root.querySelector('[data-sv-buy-data]');
      if (!dataEl) return;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      this.money = makeFormatter(this.data.moneyFormat);
      this.colourIndex = this.data.initialColour || 0;
      this.tierIndex = 0;
      this.assetBase = root.dataset.svAssetBase || '';

      this.cacheNodes();
      this.bindSwatches();
      this.bindThumbs();
      this.bindTiers();
      this.bindQuantity();
      this.sync();
    }

    cacheNodes() {
      const r = this.root;
      this.els = {
        swatches: r.querySelectorAll('[data-sv-swatch]'),
        thumbs: r.querySelectorAll('[data-sv-thumb]'),
        tiers: r.querySelectorAll('[data-sv-tier]'),
        mainImg: r.querySelector('[data-sv-main-image]'),
        colourName: r.querySelector('[data-sv-colour-name]'),
        amount: r.querySelector('[data-sv-amount]'),
        each: r.querySelector('[data-sv-each]'),
        atc: r.querySelector('[data-sv-atc]'),
        variantInput: r.querySelector('[data-sv-variant-id]'),
        qtyInput: r.querySelector('[data-sv-quantity]'),
        qtyStepper: r.querySelector('[data-sv-qty]'),
        qtyValue: r.querySelector('[data-sv-qty-value]'),
        qtySteps: r.querySelectorAll('[data-sv-qty-step]')
      };
      this.form = r.querySelector('[data-sv-form]');

      // The sticky bar lives outside the section, so it is looked up globally.
      this.sticky = {
        img: document.querySelector('[data-sv-sticky-image]'),
        price: document.querySelector('[data-sv-sticky-price]'),
        sub: document.querySelector('[data-sv-sticky-sub]'),
        form: document.querySelector('[data-sv-sticky-form]'),
        variantInput: document.querySelector('[data-sv-sticky-variant]'),
        qtyInput: document.querySelector('[data-sv-sticky-quantity]')
      };
    }

    get colour() {
      return this.data.colours[this.colourIndex];
    }

    get tier() {
      return this.data.tiers[this.tierIndex];
    }

    /**
     * Total in cents for the current colour + tier.
     *
     * A fixed-amount saving (tier.fixedAmount, already converted to cents
     * by Liquid) always wins over the percentage: unit, quantity and
     * fixedAmount are all integers, so `unit * quantity - fixedAmount` is
     * exact integer arithmetic with no rounding step to drift from the
     * total Liquid rendered on page load. The percentage path only runs
     * when no fixed amount is set, and keeps its existing rounding
     * behaviour so nothing else changes.
     */
    get total() {
      const unit = this.colour.price;
      const t = this.tier;
      const gross = unit * t.quantity;
      if (t.fixedAmount) {
        return Math.max(0, gross - t.fixedAmount);
      }
      return Math.round(gross * (1 - (t.discount || 0) / 100));
    }

    bindSwatches() {
      const list = Array.from(this.els.swatches);
      list.forEach((btn, i) => {
        btn.addEventListener('click', () => this.selectColour(i));
        btn.addEventListener('keydown', (e) => {
          const delta =
            e.key === 'ArrowRight' || e.key === 'ArrowDown'
              ? 1
              : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
                ? -1
                : 0;
          if (!delta) return;
          e.preventDefault();
          this.selectColour((i + delta + list.length) % list.length, true);
        });
      });
    }

    bindThumbs() {
      this.els.thumbs.forEach((btn, i) => {
        btn.addEventListener('click', () => this.selectColour(i));
      });
    }

    bindTiers() {
      const list = Array.from(this.els.tiers);
      list.forEach((btn, i) => {
        btn.addEventListener('click', () => this.selectTier(i));
        btn.addEventListener('keydown', (e) => {
          const delta =
            e.key === 'ArrowDown' || e.key === 'ArrowRight'
              ? 1
              : e.key === 'ArrowUp' || e.key === 'ArrowLeft'
                ? -1
                : 0;
          if (!delta) return;
          e.preventDefault();
          this.selectTier((i + delta + list.length) % list.length, true);
        });
      });
    }

    /**
     * Quantity stepper, used instead of tiers for a product bought in any
     * number rather than in fixed sets.
     *
     * It writes into the single tier the payload carries rather than adding a
     * parallel notion of quantity, so total, per-unit, the hidden `quantity`
     * field, the sticky bar and the button label keep reading `tier.quantity`
     * and there is one code path, not two.
     */
    bindQuantity() {
      if (!this.els.qtyStepper || !this.els.qtyValue) return;

      const max = Number(this.data.quantityMax) || 10;

      const apply = (n) => {
        const clamped = Math.min(Math.max(Math.round(n) || 1, 1), max);
        this.els.qtyValue.value = String(clamped);
        this.data.tiers[this.tierIndex].quantity = clamped;
        this.els.qtySteps.forEach((b) => {
          const dir = Number(b.dataset.svQtyStep);
          b.disabled = (dir < 0 && clamped === 1) || (dir > 0 && clamped === max);
        });
        this.sync();
      };

      this.els.qtySteps.forEach((b) => {
        b.addEventListener('click', () => {
          apply(Number(this.els.qtyValue.value) + Number(b.dataset.svQtyStep));
        });
      });

      // `change` rather than `input`, so a half-typed number never briefly
      // becomes the quantity that a fast add-to-cart would post.
      this.els.qtyValue.addEventListener('change', () => apply(Number(this.els.qtyValue.value)));
      this.els.qtyValue.addEventListener('blur', () => apply(Number(this.els.qtyValue.value)));

      apply(Number(this.els.qtyValue.value) || 1);
    }

    selectColour(index, focus) {
      this.colourIndex = index;
      const colour = this.colour;

      this.els.swatches.forEach((b, i) => {
        b.setAttribute('aria-checked', String(i === index));
        b.tabIndex = i === index ? 0 : -1;
      });
      this.els.thumbs.forEach((b, i) => {
        b.setAttribute('aria-selected', String(i === index));
      });

      if (this.els.colourName) this.els.colourName.textContent = colour.name;

      if (this.els.mainImg) {
        const img = this.els.mainImg;

        // assetBase is only emitted by Liquid when the guard approved this
        // product's colour slugs AND no merchant override image is in play
        // (see sections/sv-buy.liquid). When it is absent, building a
        // sv-photo-colour-<slug>-* URL here would either 404 (slug not in
        // known_colour_slugs) or silently overwrite the merchant's chosen
        // override on first click — both bugs this guard exists to prevent.
        // Fall back to the per-colour product image Liquid already emitted
        // into the JSON data contract instead.
        // The slug comes from Liquid, which folds translated names ("Grau")
        // onto the English asset slug; the displayed name is localised and
        // cannot be slugged here. A colour without a known slug keeps its
        // product image rather than requesting an asset that does not exist.
        const slug = colour.slug || '';
        const useAsset = Boolean(this.assetBase) && Boolean(slug);
        const src = useAsset ? svColourSrc(this.assetBase, slug) : colour.image;
        const srcset = useAsset ? svColourSrcset(this.assetBase, slug) : '';

        if (src) {
          img.style.opacity = '0';
          window.setTimeout(function () {
            img.src = src;
            img.srcset = srcset;
            img.alt = colour.alt || colour.name;
            img.style.opacity = '1';
          }, 160);
        }
      }

      if (this.sticky.img && colour.thumb) this.sticky.img.src = colour.thumb;
      if (focus && this.els.swatches[index]) this.els.swatches[index].focus();

      this.sync();
    }

    selectTier(index, focus) {
      this.tierIndex = index;
      this.els.tiers.forEach((b, i) => {
        b.setAttribute('aria-checked', String(i === index));
        b.tabIndex = i === index ? 0 : -1;
      });
      if (focus && this.els.tiers[index]) this.els.tiers[index].focus();
      this.sync();
    }

    sync() {
      const colour = this.colour;
      const tier = this.tier;
      const total = this.total;
      const each = Math.round(total / tier.quantity);

      if (this.els.amount) this.els.amount.textContent = this.money(total);
      if (this.els.each) {
        this.els.each.textContent = this.data.strings.each.replace('[price]', this.money(each));
      }
      if (this.els.atc) {
        this.els.atc.textContent = colour.available
          ? this.data.strings.addToCart.replace('[price]', this.money(total))
          : this.data.strings.soldOut;
        this.els.atc.disabled = !colour.available;
      }

      if (this.els.variantInput) {
        this.els.variantInput.value = colour.id;
        this.els.variantInput.disabled = !colour.available;
      }

      /* Quantity has to be written in two places, and they do different jobs.
         The hidden input is what actually reaches /cart/add, because Horizon
         posts `new FormData(form)`. The component's data-quantity-default only
         feeds the "n items added" announcement and the queued-add path. Set
         one without the other and the cart quietly disagrees with the page. */
      if (this.els.qtyInput) this.els.qtyInput.value = String(tier.quantity);
      if (this.sticky.qtyInput) this.sticky.qtyInput.value = String(tier.quantity);
      if (this.form) this.form.dataset.quantityDefault = String(tier.quantity);
      if (this.sticky.form) this.sticky.form.dataset.quantityDefault = String(tier.quantity);

      /* Horizon prefers a ?variant= URL parameter over the hidden field, so it
         is kept in step — otherwise picking a colour on the product page would
         add whichever variant was in the URL when the page loaded. */

      if (window.history && window.location.search.includes('variant=')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', colour.id);
        window.history.replaceState({}, '', url);
      }

      if (this.sticky.price) this.sticky.price.textContent = this.money(total);
      if (this.sticky.sub) this.sticky.sub.textContent = colour.name + ' · ' + tier.label;
      if (this.sticky.variantInput) {
        this.sticky.variantInput.value = colour.id;
        this.sticky.variantInput.disabled = !colour.available;
      }
    }
  }

  /* --------------------------------------------------------- sticky bar ---
     Shown once the hero has scrolled away, and hidden again whenever the real
     buy block is on screen. Two competing primary CTAs on one screen is worse
     than none.
  ------------------------------------------------------------------------- */

  function initStickyBar() {
    const bar = document.querySelector('[data-sv-stickybar]');
    if (!bar || !('IntersectionObserver' in window)) return;

    const hero = document.querySelector('[data-sv-hero]');
    const buy = document.querySelector('[data-sv-buy]');

    let pastHero = !hero;
    let overBuy = false;

    function paint() {
      bar.classList.toggle('is-on', pastHero && !overBuy);
    }

    if (hero) {
      new IntersectionObserver(
        function (entries) {
          pastHero = !entries[0].isIntersecting;
          paint();
        },
        { threshold: 0 }
      ).observe(hero);
    }

    if (buy) {
      new IntersectionObserver(
        function (entries) {
          overBuy = entries[0].isIntersecting;
          paint();
        },
        { threshold: 0 }
      ).observe(buy);
    }

    paint();
  }

  /* ------------------------------------------------------------------ boot */

  function boot(root) {
    (root || document).querySelectorAll('[data-sv-buy]').forEach(function (el) {
      if (el.hasAttribute('data-sv-bound')) return;
      el.setAttribute('data-sv-bound', '');
      new SomveraBuy(el);
    });
    initStickyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
